import type { DeviceInfo } from '@capacitor/device';
import { Device } from '@capacitor/device';
import { Button, Textarea } from '@flexo/atoms';
import { StarRating } from '@flexo/molecules';
import { useTranslation } from 'react-i18next';
import styles from './feedback-page.module.scss';
// import { AppReview } from '@capawesome/capacitor-app-review';

import { useEffect, useMemo, useState } from 'react';
import { submitFeedback as submitFeedbackHelper } from './submit-feedback.helper';

import { Player } from '@lottiefiles/react-lottie-player';

import { App } from '@capacitor/app';
import { ALERT, RIGHT_DRAWER } from '@flexo/reducers';
import { Checkbox } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

const ENV = (import.meta as any).env;

interface FeedbackPageProps {
  thankYou?: string;
  confirmationDescription?: string;
  doneButton?: string;
  confirmButton?: string;
  doneConfirmationDescription?: string;
  label_1?: string;
  comment_label?: string;
  comment_description?: string;
  checkbox?: boolean;
  checkbox_label?: string;
  setProfileSection?: (section: string) => void;
  backgroundColor?: string;
  containedLabel?: boolean;
  checkboxColor?: string;
}

export function FeedbackPage(props: FeedbackPageProps) {
  const {
    FeedbackPage__Content,
    FeedbackPage__Rating,
    FeedbackPage__Rating__Title,
    FeedbackPage__Rating__Stars,
    FeedbackPage__Footer,
    FeedbackPage__Footer__Description,
    FeedbackPage__Checkbox,
  } = styles;
  const { t } = useTranslation();
  const user = useSelector((state: any) => state.user);
  const dispatch = useDispatch();
  const [feedback, setFeedback] = useState<number | null>(null);
  const [sendingFeedback, setSendingFeedback] = useState(false);
  const [inputData, setInputData] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const metadata = useSelector((state: any) => state.metadata);
  const [version, setVersion] = useState('');

  const loader = '/loaderLottie.json';

  function parseIosFromUserAgent() {
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const isIphone = /iPhone/i.test(ua);
    const isIpad = /iPad/i.test(ua);
    const isIpod = /iPod/i.test(ua);
    const match = ua.match(/OS\s([\d_]+)/i);
    const osVersion = match ? match[1].replace(/_/g, '.') : undefined;
    const model = isIphone
      ? 'iPhone'
      : isIpad
      ? 'iPad'
      : isIpod
      ? 'iPod'
      : undefined;
    const osName = isIphone || isIpad || isIpod ? 'iOS' : undefined;
    return { model, osName, osVersion } as const;
  }

  async function getDeviceInfo(): Promise<DeviceInfo | 'Not available'> {
    //TODO fix this so device info is not undefined
    try {
      const info = await Device.getInfo();
      return info;
    } catch {
      return 'Not available';
    }
  }

  const submitFeedback = async () => {
    setSendingFeedback(true);
    try {
      await submitFeedbackHelper({
        ENV,
        metadata,
        version,
        inputData,
        feedback,
        user,
        getDeviceInfo,
        parseIosFromUserAgent,
        dispatch,
        ALERT,
      });

      if (ENV?.VITE_APP_IS_WEB === 'true') {
        dispatch(RIGHT_DRAWER.HideDrawer());
        setTimeout(() => {
          dispatch(
            ALERT.SetAlert({
              value: {
                type: 'BANNER_FEEDBACK_SUBMITTED',
              } as any,
            })
          );
        }, 200);
      } else {
        if (props.setProfileSection) {
          props.setProfileSection('UNSET');
        }
        setTimeout(() => {
          console.log('BANNER_FEEDBACK_SUBMITTED');
          dispatch(
            ALERT.SetAlert({
              value: {
                type: 'BANNER_FEEDBACK_SUBMITTED',
              } as any,
            })
          );
        }, 200);
      }
    } catch {
      // handled inside helper
    } finally {
      setSendingFeedback(false);
    }
  };
  const handleStarRatingChange = (rating: number | null) => {
    setFeedback(rating);
  };
  const isDisabled = useMemo(() => {
    if (props.checkbox && !termsAccepted) return true;
    if (sendingFeedback) return true;
    return !feedback;
  }, [props.checkbox, termsAccepted, sendingFeedback, feedback]);

  const onFeedbackClick = async () => {
    await submitFeedback();
    if (feedback === 5) {
      try {
        const { AppReview } = await import('@capawesome/capacitor-app-review');
        await AppReview.requestReview();
      } catch {
        console.warn('App Review not available');
      }
    }
  };

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const info = await App.getInfo();
        setVersion(`${info.version} (${info.build})`); // Version and build number
      } catch {
        // Fall back to environment variables if available
        setVersion(ENV.VITE_APP_VERSION ? `${ENV.VITE_APP_VERSION}` : '');
      }
    };

    fetchVersion();
  }, []);

  return (
    <div
      className={`${styles.FeedbackPage} ${
        props.containedLabel ? styles.FeedbackPage__Contained : ''
      }`}
      style={{ backgroundColor: props.backgroundColor }}
    >
      <div className={FeedbackPage__Content}>
        <div className={FeedbackPage__Rating}>
          <span className={`${FeedbackPage__Rating__Title} heading4`}>
            {t(props.label_1 as any)}
          </span>
          <div className={FeedbackPage__Rating__Stars}>
            <StarRating onChange={handleStarRatingChange} />
          </div>
        </div>
        <div>
          <Textarea
            name="feedback"
            label={t(props.comment_label as any) as any}
            variant={props.containedLabel ? 'containedLabel' : 'default'}
            className="paragraph"
            onChange={(e) => setInputData(e.target.value)}
            value={inputData}
            description={t(props.comment_description as any) as any}
          />
        </div>
        {props.checkbox && (
          <div
            className={FeedbackPage__Checkbox}
            onClick={() => setTermsAccepted(!termsAccepted)}
          >
            <Checkbox
              checked={termsAccepted}
              onChange={() => setTermsAccepted(!termsAccepted)}
              onClick={() => setTermsAccepted(!termsAccepted)}
              style={{
                height: '12px',
                width: '12px',
                marginRight: '8px',
                // color: `var(--hive-${CHECKBOX_COLOR}-500)`,
              }}
            />
            <span className={`${styles.FeedbackPage__Checkbox__Label} detail`}>
              {t(props.checkbox_label as any)}
            </span>
          </div>
        )}
      </div>

      <div className={FeedbackPage__Footer}>
        <Button
          label={
            sendingFeedback ? (
              <Player
                autoplay
                loop
                src={loader}
                style={{
                  height: 20,
                  width: '100px',
                  filter: 'brightness(90%)',
                }}
              />
            ) : (
              t(props.confirmButton as any)
            )
          }
          size="micro"
          onClick={onFeedbackClick}
          disabled={isDisabled}
          variant="primary"
          color="secondary"
        />

        <span className={`${FeedbackPage__Footer__Description} detail`}>
          {t(props.confirmationDescription as any)}
        </span>
      </div>
    </div>
  );
}

export default FeedbackPage;
